class BtlDepartmentNavigation {
  constructor(options) {
    Object.assign(this, options);
    this.url = new URL(options.current);
    this.destinations = new Set(options.destinations.map(value => {
      const url = new URL(value, this.url);
      return url.origin + url.pathname + url.search;
    }));
    this.version = 0;
  }

  canHandle(value, historyRestore = false) {
    try {
      const url = new URL(value, this.url);
      return url.origin === this.url.origin && (historyRestore || !url.hash) &&
        this.destinations.has(url.origin + url.pathname + url.search);
    } catch { return false; }
  }

  cancel() {
    if (!this.controller) return;
    ++this.version;
    this.controller.abort();
    this.controller = null;
    this.paint(this.url);
    this.busy(false);
  }

  async go(value, options = {}) {
    if (!this.canHandle(value, options.record === false)) return false;
    const url = new URL(value, this.url);
    const version = ++this.version;
    this.controller?.abort();
    this.controller = null;
    this.paint(url);
    if (url.origin + url.pathname + url.search === this.url.origin + this.url.pathname + this.url.search) {
      this.url = url;
      this.busy(false);
      return true;
    }
    const controller = new AbortController();
    this.controller = controller;
    this.busy(true, url);
    let timer;
    try {
      const page = await Promise.race([
        this.load(url, controller.signal),
        new Promise((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error('Department navigation timed out'));
          }, this.timeout || 10000);
        }),
      ]);
      if (version !== this.version) return false;
      this.commit(page, url);
      if (options.record !== false) this.record(url, options);
      this.url = url;
      this.finish?.(url, options);
      return true;
    } catch {
      if (version !== this.version) return false;
      this.paint(this.url);
      this.fallback(url);
      return false;
    } finally {
      clearTimeout(timer);
      if (version === this.version) {
        this.controller = null;
        this.busy(false);
      }
    }
  }
}

(() => {
  const nav = document.querySelector('btl-luxury-header[data-mobile-subpage="false"] .btlh__store-switcher');
  const main = document.querySelector('#MainContent');
  if (!nav || !main || window.Shopify?.designMode || nav.dataset.departmentEnhanced || typeof HTMLUpdateUtility === 'undefined') return;
  const header = nav.closest('btl-luxury-header');
  const links = [...nav.querySelectorAll('a[data-department]')];
  if (links.length !== 2 || links.some(link => !link.dataset.activeShape || !link.dataset.inactiveShape)) return;
  nav.dataset.departmentEnhanced = 'true';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const easing = 'cubic-bezier(.22,.61,.36,1)';
  const status = document.createElement('span');
  status.className = 'visually-hidden';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  header.append(status);
  const skeleton = document.createElement('div');
  skeleton.className = 'btl-department-skeleton';
  skeleton.setAttribute('aria-hidden', 'true');
  skeleton.innerHTML = '<div class="btl-department-skeleton__home"><div class="btl-department-skeleton__categories"><span></span><span></span></div><div class="btl-department-skeleton__hero"></div></div>' +
    '<div class="btl-department-skeleton__gift"><div class="btl-department-skeleton__heading"></div><div class="btl-department-skeleton__grid">' +
    '<div><div class="btl-department-skeleton__image"></div><div class="btl-department-skeleton__line"></div></div>'.repeat(3) + '</div></div>';
  let loadingTarget;
  let loadingScroll = 0;
  let focusContent = false;
  // Section styles must survive replacing the content that originally loaded them.
  main.querySelectorAll('link[rel="stylesheet"][href]').forEach(node => document.head.append(node));
  const assets = new Map([...document.querySelectorAll('script[src],link[rel="stylesheet"][href]')]
    .map(node => [node.src || node.href, Promise.resolve()]));

  function paint(url) {
    const selected = links.find(link => new URL(link.href).pathname === url.pathname);
    if (!selected) return;
    const palette = getComputedStyle(nav);
    const surface = palette.getPropertyValue('--department-' + selected.dataset.department + '-bg').trim();
    const ink = palette.getPropertyValue('--department-' + selected.dataset.department + '-ink').trim();
    // Keep SVG paint servers mounted; cloning masked gradients can flash black in Safari.
    for (const link of links) {
      const svg = link.querySelector('.btlh__department-shape');
      const active = link === selected;
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
      svg?.querySelectorAll('path').forEach(path => path.setAttribute('d', active ? link.dataset.activeShape : link.dataset.inactiveShape));
    }
    header.style.setProperty('--department-surface', surface);
    header.style.setProperty('--department-surface-ink', ink);
  }

  function busy(value, url) {
    main.setAttribute('aria-busy', String(value));
    const selected = links.find(link => link.hasAttribute('aria-current'));
    if (value) {
      if (!main.hasAttribute('data-department-loading')) {
        loadingScroll = window.scrollY;
        focusContent = main.contains(document.activeElement);
      }
      loadingTarget = url.href;
      skeleton.dataset.kind = selected.dataset.department;
      main.append(skeleton);
      main.setAttribute('data-department-loading', '');
      status.textContent = 'Loading ' + selected.textContent.trim();
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      main.removeAttribute('data-department-loading');
      skeleton.remove();
      if (loadingTarget && router.url.href !== loadingTarget) {
        window.scrollTo({ top: loadingScroll, behavior: 'instant' });
        if (focusContent) main.focus({ preventScroll: true });
      }
      loadingTarget = null;
      focusContent = false;
      status.textContent = selected.textContent.trim() + ' selected';
    }
  }

  function loadAsset(node, base) {
    const url = new URL(node.src || node.href, base);
    if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) throw new Error('Unsupported section asset');
    if (assets.has(url.href)) return assets.get(url.href);
    const pending = new Promise((resolve, reject) => {
      const copy = document.createElement(node.tagName.toLowerCase());
      for (const attribute of [...node.attributes]) {
        if (!attribute.name.startsWith('on')) copy.setAttribute(attribute.name, attribute.value);
      }
      if (copy.tagName === 'LINK' && node.getAttribute('onload')?.includes('media')) copy.media = 'all';
      copy.onload = resolve;
      copy.onerror = reject;
      if (copy.tagName === 'SCRIPT') { copy.src = url.href; copy.async = false; }
      else copy.href = url.href;
      document.head.append(copy);
    }).catch(error => { assets.delete(url.href); throw error; });
    assets.set(url.href, pending);
    return pending;
  }

  async function load(url, signal) {
    const response = await fetch(url.href, { signal, credentials: 'same-origin', redirect: 'error', headers: { Accept: 'text/html' } });
    if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Unavailable department');
    const resolved = new URL(response.url);
    if (resolved.origin !== location.origin || resolved.pathname !== url.pathname) throw new Error('Unexpected department destination');
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const content = page.querySelector('#MainContent');
    if (!content || !page.querySelector('btl-luxury-header[data-mobile-subpage="false"] .btlh__store-switcher')) throw new Error('Unsupported department template');
    const resources = [...content.querySelectorAll('script[src],link[rel="stylesheet"][href]')];
    for (const resource of page.head.querySelectorAll('script[src],link[rel="stylesheet"][href]')) {
      const source = resource.src || resource.href;
      if (source.includes('/cdn/shop/t/') || source.includes('/compiled_assets/')) resources.push(resource);
    }
    for (const resource of resources) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      await loadAsset(resource, url);
      if (content.contains(resource)) resource.remove();
    }
    return page;
  }

  function commit(page) {
    const content = page.querySelector('#MainContent');
    focusContent = focusContent || main.contains(document.activeElement);
    main.querySelectorAll('.shopify-section').forEach(section => section.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: { sectionId: section.id.replace('shopify-section-', '') } })));
    // The HTML is rendered by the configured same-origin Shopify destination.
    HTMLUpdateUtility.setInnerHTML(main, content.innerHTML);
    main.dataset.template = content.dataset.template || '';
    document.title = page.title;
    for (const selector of ['link[rel="canonical"]', 'meta[name="description"]', 'meta[property="og:title"]', 'meta[property="og:url"]', 'meta[property="og:description"]', 'meta[property="og:image"]', 'meta[name="twitter:title"]', 'meta[name="twitter:description"]', 'meta[name="twitter:image"]']) {
      const next = page.head.querySelector(selector);
      const existing = document.head.querySelector(selector);
      if (next) { if (existing) existing.replaceWith(next.cloneNode(true)); else document.head.append(next.cloneNode(true)); }
      else existing?.remove();
    }
    main.querySelectorAll('.shopify-section').forEach(section => section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: section.id.replace('shopify-section-', '') } })));
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(main);
    if (!motion.matches) main.animate([{ opacity: .88 }, { opacity: 1 }], { duration: 180, easing });
  }

  const router = new BtlDepartmentNavigation({
    current: location.href,
    destinations: links.map(link => link.href),
    paint, busy, load, commit,
    record(url) {
      history.replaceState({ ...history.state, btlDepartment: true, scrollY: loadingScroll }, '', location.href);
      history.pushState({ btlDepartment: true, scrollY: 0 }, '', url.href);
    },
    finish(url, options) {
      window.scrollTo({ top: options.scroll || 0, behavior: 'instant' });
      if (focusContent) main.focus({ preventScroll: true });
      try {
        if (window.StandardEvents?.PageViewEvent) document.dispatchEvent(new StandardEvents.PageViewEvent({ page: { template: main.dataset.template, title: document.title, url: url.href } }));
      } catch { /* Analytics must not interrupt navigation. */ }
      document.dispatchEvent(new CustomEvent('btl:department:change', { detail: { url: url.href } }));
    },
    fallback(url) {
      if (location.href === url.href) location.reload();
      else location.assign(url.href);
    },
  });
  if (!router.canHandle(location.href)) return;
  history.replaceState({ ...history.state, btlDepartment: true, scrollY: window.scrollY }, '', location.href);
  document.addEventListener('click', event => {
    const targetLink = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (targetLink && !nav.contains(targetLink) && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) router.cancel();
    const link = event.target instanceof Element ? event.target.closest('.btlh__store-switcher a[data-department]') : null;
    if (!link || !nav.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self') || !router.canHandle(link.href)) return;
    event.preventDefault();
    router.go(link.href, { fromScroll: window.scrollY });
  }, true);
  window.addEventListener('popstate', event => {
    if (event.state?.btlDepartment && router.canHandle(location.href, true)) router.go(location.href, { record: false, scroll: event.state.scrollY || 0 });
  });
  window.addEventListener('pagehide', () => router.cancel());
})();
